import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Booking() {
  const { t } = useTranslation('common');
  const [formData, setFormData] = useState({
    service: '',
    date: '',
    time: '',
    frequency: 'one-time',
    name: '',
    phone: '',
    address: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5i0ddbgekujqjrtsrmg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          service: '',
          date: '',
          time: '',
          frequency: 'one-time',
          name: '',
          phone: '',
          address: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section id="booking" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Images */}
            <div className="space-y-6">
              <div className="relative h-[600px] rounded-3xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/1c1efe20ab982279d0e71dac7f6c0a80/d618048418e5c96276a16e06d38f3b87.png"
                  alt="Art Throng Home Service"
                  className="w-full h-full object-contain object-center bg-gray-50"
                />
              </div>
              <div className="bg-gray-50 rounded-2xl p-6">
                <p className="text-[#2B2B2B] font-semibold text-lg mb-2">
                  Trusted by 100+ homes across Fuerteventura and Lanzarote
                </p>
                <div className="flex items-center space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className="ri-star-fill text-yellow-400 text-xl"></i>
                  ))}
                  <span className="ml-2 text-gray-600 font-medium">4.9/5 rating</span>
                </div>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="bg-gray-50 rounded-3xl p-8 lg:p-12">
              <h2 className="font-serif text-4xl text-[#2B2B2B] mb-8">
                {t('booking.title')}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="booking-form">
                {/* Service Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('booking.form.service')}
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    className="w-full px-4 py-4 rounded-lg border border-gray-300 focus:outline-none focus:border-[#8B9B7E] text-gray-700 cursor-pointer"
                    required
                  >
                    <option value="">{t('booking.form.servicePlaceholder')}</option>
                    <option value="interior-design">{t('booking.services.interior')}</option>
                    <option value="cleaning">{t('booking.services.cleaning')}</option>
                    <option value="maintenance">{t('booking.services.maintenance')}</option>
                    <option value="renovation">{t('booking.services.renovation')}</option>
                    <option value="post-stay-refresh">{t('packages.items.postStay.name')}</option>
                    <option value="villa-prep">{t('packages.items.villaPrep.name')}</option>
                    <option value="monthly-refresh">{t('packages.items.monthlyRefresh.name')}</option>
                  </select>
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('booking.form.date')}
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="w-full px-4 py-4 rounded-lg border border-gray-300 focus:outline-none focus:border-[#8B9B7E] text-gray-700"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('booking.form.time')}
                    </label>
                    <input
                      type="time"
                      name="time"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      className="w-full px-4 py-4 rounded-lg border border-gray-300 focus:outline-none focus:border-[#8B9B7E] text-gray-700"
                      required
                    />
                  </div>
                </div>

                {/* Frequency */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    {t('booking.form.frequency')}
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: 'one-time', label: t('booking.frequencies.oneTime') },
                      { value: 'weekly', label: t('booking.frequencies.weekly') },
                      { value: 'bi-weekly', label: t('booking.frequencies.biweekly') },
                      { value: 'monthly', label: t('booking.frequencies.monthly') },
                    ].map((option) => (
                      <label
                        key={option.value}
                        className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 cursor-pointer transition-all ${
                          formData.frequency === option.value
                            ? 'border-[#8B9B7E] bg-[#8B9B7E]/10'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="radio"
                          name="frequency"
                          value={option.value}
                          checked={formData.frequency === option.value}
                          onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                          className="sr-only"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          {option.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Contact Fields */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('booking.form.name')}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={t('booking.form.namePlaceholder')}
                    className="w-full px-4 py-4 rounded-lg border border-gray-300 focus:outline-none focus:border-[#8B9B7E] text-gray-700"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('booking.form.phone')}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder={t('booking.form.phonePlaceholder')}
                    className="w-full px-4 py-4 rounded-lg border border-gray-300 focus:outline-none focus:border-[#8B9B7E] text-gray-700"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('booking.form.message')}
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder={t('booking.form.messagePlaceholder')}
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-4 rounded-lg border border-gray-300 focus:outline-none focus:border-[#8B9B7E] text-gray-700 resize-none"
                    required
                  ></textarea>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#8B9B7E] text-white py-4 rounded-lg font-semibold hover:bg-[#7a8a6d] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? t('booking.form.submitting') : t('booking.form.submit')}
                </button>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <p className="text-green-600 text-center text-sm">
                    {t('booking.form.success')}
                  </p>
                )}
                {submitStatus === 'error' && (
                  <p className="text-red-600 text-center text-sm">
                    {t('booking.form.error')}
                  </p>
                )}

                <p className="text-gray-500 text-xs text-center">
                  Secure payment via Razorpay • Cancel anytime
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Our Team Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-5xl text-[#2B2B2B] mb-4">
              Our Team
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Meet the skilled professionals who bring your vision to life
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Team Photo 1 */}
            <div className="relative h-[500px] rounded-3xl overflow-hidden group">
              <img
                src="https://static.readdy.ai/image/1c1efe20ab982279d0e71dac7f6c0a80/6438626365eb00ec22b3a496db8dd73a.jpeg"
                alt="Art Throng Team at Work"
                className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
              <div className="absolute bottom-8 left-8 right-8">
                <p className="text-white font-semibold text-xl">
                  Expert Craftsmen at Work
                </p>
                <p className="text-white/90 text-sm mt-2">
                  Our skilled team delivering quality results
                </p>
              </div>
            </div>

            {/* Team Photo 2 */}
            <div className="relative h-[500px] rounded-3xl overflow-hidden group">
              <img
                src="https://static.readdy.ai/image/1c1efe20ab982279d0e71dac7f6c0a80/f2925fa4285d28df3ac792577d0b3b80.jpeg"
                alt="Art Throng Team Collaboration"
                className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
              <div className="absolute bottom-8 left-8 right-8">
                <p className="text-white font-semibold text-xl">
                  Dedicated Professionals
                </p>
                <p className="text-white/90 text-sm mt-2">
                  Albañiles, multilingual team fluent in English, Spanish & Italian
                </p>
              </div>
            </div>

            {/* Team Photo 3 - Designer */}
            <div className="relative h-[500px] rounded-3xl overflow-hidden group">
              <img
                src="https://static.readdy.ai/image/1c1efe20ab982279d0e71dac7f6c0a80/0480f1eef222d4914fcee14c559d5937.jpeg"
                alt="Art Throng Designer"
                className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
              <div className="absolute bottom-8 left-8 right-8">
                <p className="text-white font-semibold text-xl">
                  Designer
                </p>
                <p className="text-white/90 text-sm mt-2">
                  Creative vision and innovative interior design solutions
                </p>
              </div>
            </div>
          </div>

          {/* Team Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
            <div className="text-center">
              <p className="font-serif text-4xl text-[#8B9B7E] mb-2">5+</p>
              <p className="text-gray-600 font-medium">Years Experience</p>
            </div>
            <div className="text-center">
              <p className="font-serif text-4xl text-[#8B9B7E] mb-2">100+</p>
              <p className="text-gray-600 font-medium">Happy Clients</p>
            </div>
            <div className="text-center">
              <p className="font-serif text-4xl text-[#8B9B7E] mb-2">15+</p>
              <p className="text-gray-600 font-medium">Team Members</p>
            </div>
            <div className="text-center">
              <p className="font-serif text-4xl text-[#8B9B7E] mb-2">3</p>
              <p className="text-gray-600 font-medium">Languages</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
